/**
 * Site themes — the tokens, their vocabulary, and the two rules that keep any
 * combination of them readable.
 *
 * A theme is not a stylesheet. It is a short list of decisions, each with a
 * closed set of answers, which the SPA stamps onto <html> as data attributes and
 * CSS turns into layout and type. Keeping the vocabulary here — rather than in
 * the admin form, the writer's customiser and the CSS separately — means adding
 * a value is one edit, and means the Worker validates exactly what the browser
 * knows how to render.
 *
 * Themes are curated by an admin and chosen by a writer, who may then override
 * individual tokens for their own site (see `resolveTheme`). Everything is
 * bounded on purpose: a writer can revamp their site without being able to
 * produce something illegible in Devanagari.
 */

/**
 * How a writer's front page is arranged.
 *
 * `classic`, `minimal` and `journal` came first and are unchanged. The three
 * after them exist because a single chronological list serves a कवि and a
 * यात्रासाहित्य writer equally badly: one wants the poems themselves visible,
 * the other wants their photographs, and someone with two hundred मुक्तक wants
 * an index they can scan.
 */
export const SITE_LAYOUTS = ['classic', 'minimal', 'journal', 'magazine', 'gallery', 'index'] as const;
export type SiteLayout = (typeof SITE_LAYOUTS)[number];

export const BODY_FONTS = ['serif', 'sans'] as const;
export type BodyFont = (typeof BODY_FONTS)[number];

/** Headings may take the editorial face (Martel), the UI face, or the body serif. */
export const HEADING_FONTS = ['sans', 'serif', 'headline'] as const;
export type HeadingFont = (typeof HEADING_FONTS)[number];

export const DENSITIES = ['compact', 'comfortable', 'spacious'] as const;
export type Density = (typeof DENSITIES)[number];

export const CORNERS = ['sharp', 'soft', 'round'] as const;
export type Corner = (typeof CORNERS)[number];

/**
 * A tint over the page, not a colour scheme.
 *
 * The reader's own light/sepia/dark choice always wins — see the note on
 * `resolveTheme`. This only warms or cools the paper by a few degrees of hue,
 * the way stock differs between a poetry collection and a technical journal.
 */
export const PAPERS = ['default', 'warm', 'cool'] as const;
export type Paper = (typeof PAPERS)[number];

/** What separates stanzas and closes a piece. */
export const ORNAMENTS = ['nib', 'rule', 'asterism', 'none'] as const;
export type Ornament = (typeof ORNAMENTS)[number];

/**
 * Design tokens a site theme controls.
 *
 * Deliberately a closed set: a handful of decisions that change how a writer's
 * site feels without letting anyone produce something illegible in Devanagari.
 */
export interface TemplateConfig {
  /** HSL triplet, e.g. "349 62% 38%" — see `accentPair` for how it is applied. */
  accent: string;
  bodyFont: BodyFont;
  headingFont: HeadingFont;
  layout: SiteLayout;
  showCovers: boolean;
  density: Density;
  corners: Corner;
  paper: Paper;
  ornament: Ornament;
}

/**
 * What a site looks like with no theme at all.
 *
 * Also the merge floor: a template row written before a token existed is filled
 * in from here rather than rendering as `undefined`, so adding a token never
 * needs a data migration.
 */
export const DEFAULT_TEMPLATE_CONFIG: TemplateConfig = {
  accent: '349 62% 38%',
  bodyFont: 'serif',
  headingFont: 'sans',
  layout: 'classic',
  showCovers: true,
  density: 'comfortable',
  corners: 'soft',
  paper: 'default',
  ornament: 'nib',
};

export const LAYOUT_META: Record<SiteLayout, { np: string; en: string; hint: string }> = {
  classic: { np: 'शास्त्रीय', en: 'Classic', hint: 'शीर्षक, अंश र मिति — एउटै स्तम्भमा' },
  minimal: { np: 'सरल', en: 'Minimal', hint: 'कभर बिना, साँघुरो स्तम्भ — शब्द मात्र' },
  journal: { np: 'डायरी', en: 'Journal', hint: 'मितिअनुसार, हरेक प्रविष्टि छुट्टै' },
  magazine: { np: 'पत्रिका', en: 'Magazine', hint: 'दुई स्तम्भको ग्रिड, कभरसहित' },
  gallery: { np: 'चित्रशाला', en: 'Gallery', hint: 'ठूला कभर अगाडि — यात्रा र फोटोका लागि' },
  index: { np: 'सूची', en: 'Index', hint: 'एक हरफ एक रचना — ठूलो सङ्ग्रहका लागि' },
};

export const DENSITY_META: Record<Density, { np: string; en: string }> = {
  compact: { np: 'कसिलो', en: 'Compact' },
  comfortable: { np: 'सहज', en: 'Comfortable' },
  spacious: { np: 'फराकिलो', en: 'Spacious' },
};

export const CORNER_META: Record<Corner, { np: string; en: string }> = {
  sharp: { np: 'सिधा', en: 'Sharp' },
  soft: { np: 'नरम', en: 'Soft' },
  round: { np: 'गोलो', en: 'Round' },
};

export const PAPER_META: Record<Paper, { np: string; en: string }> = {
  default: { np: 'सामान्य', en: 'Default' },
  warm: { np: 'न्यानो', en: 'Warm' },
  cool: { np: 'चिसो', en: 'Cool' },
};

export const ORNAMENT_META: Record<Ornament, { np: string; en: string }> = {
  nib: { np: 'निब', en: 'Nib' },
  rule: { np: 'रेखा', en: 'Rule' },
  asterism: { np: 'तारक', en: 'Asterism' },
  none: { np: 'केही पनि होइन', en: 'None' },
};

export const BODY_FONT_META: Record<BodyFont, { np: string; en: string }> = {
  serif: { np: 'सेरिफ', en: 'Serif' },
  sans: { np: 'स्यान्स', en: 'Sans' },
};

export const HEADING_FONT_META: Record<HeadingFont, { np: string; en: string }> = {
  sans: { np: 'स्यान्स', en: 'Sans' },
  serif: { np: 'सेरिफ', en: 'Serif' },
  headline: { np: 'सम्पादकीय', en: 'Editorial' },
};

/**
 * The accents on offer.
 *
 * A free colour picker would be the obvious thing and the wrong one: half the
 * gamut fails contrast against one of the three reading surfaces, and the writer
 * choosing it has no way to know which half. These are hand-picked at a
 * lightness that clamps cleanly in both directions (see `accentPair`), and they
 * are named, because "इनार" is a choice a writer can make and "#1D4E6B" is not.
 */
export const ACCENT_PRESETS: { value: string; np: string; en: string }[] = [
  { value: '349 62% 38%', np: 'सिन्दूर', en: 'Crimson' },
  { value: '25 75% 42%', np: 'साँझ', en: 'Amber' },
  { value: '43 74% 34%', np: 'तोरी', en: 'Mustard' },
  { value: '150 45% 30%', np: 'तुलसी', en: 'Basil' },
  { value: '190 60% 30%', np: 'इनार', en: 'Well blue' },
  { value: '243 55% 45%', np: 'नील', en: 'Indigo' },
  { value: '280 40% 42%', np: 'बैजनी', en: 'Violet' },
  { value: '340 45% 42%', np: 'गुलाफ', en: 'Rose' },
  { value: '15 55% 35%', np: 'माटो', en: 'Terracotta' },
  { value: '215 25% 30%', np: 'खरानी', en: 'Slate' },
  { value: '95 30% 28%', np: 'वन', en: 'Forest' },
  { value: '24 10% 20%', np: 'मसी', en: 'Ink' },
];

/** Matches the HSL triplet the tokens are written as. */
export const ACCENT_PATTERN = /^\d{1,3} \d{1,3}% \d{1,3}%$/;

interface Hsl {
  h: number;
  s: number;
  l: number;
}

function parseAccent(accent: string): Hsl | null {
  const match = /^(\d{1,3}) (\d{1,3})% (\d{1,3})%$/.exec(accent.trim());
  if (!match) return null;
  const [h, s, l] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (h > 360 || s > 100 || l > 100) return null;
  return { h, s, l };
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * One accent, two surfaces.
 *
 * A theme stores a single colour, but it has to sit on paper *and* on the dark
 * surface — and a crimson at 38% lightness that reads beautifully on white is
 * nearly invisible on near-black. The design system already knew this (`.dark`
 * ships its own lighter `--primary`), but a tenant theme used to overwrite that
 * with one value for both, which is how a writer's own site ended up with links
 * they could not read at night.
 *
 * So each accent is clamped into a band per surface rather than trusted as
 * given: dark enough on paper that white text holds on a primary button, light
 * enough on the dark surface that it reads as a colour at all. Hue is never
 * touched — that is the choice the writer actually made.
 */
export function accentPair(accent: string): { light: string; dark: string } {
  const parsed = parseAccent(accent) ?? parseAccent(DEFAULT_TEMPLATE_CONFIG.accent)!;
  const { h } = parsed;
  const s = clamp(parsed.s, 18, 85);

  return {
    light: `${h} ${s}% ${clamp(parsed.l, 24, 46)}%`,
    // Saturation comes down a little as lightness goes up: a fully saturated
    // colour at 60% lightness glares against a near-black page.
    dark: `${h} ${clamp(s - 4, 16, 70)}% ${clamp(parsed.l, 54, 70)}%`,
  };
}

/** True when `accent` is a well-formed HSL triplet this app can render. */
export function isValidAccent(accent: string): boolean {
  return parseAccent(accent) !== null;
}

/**
 * The theme a site is actually rendered with.
 *
 * Three layers, each narrower than the last: the defaults above, the theme an
 * admin curated, and the tokens this writer changed for themselves. Unknown
 * values are dropped rather than passed through — a token from a newer build, or
 * one edited into the database by hand, would otherwise become a data attribute
 * that matches no CSS rule and silently produces a half-applied theme.
 */
export function resolveTheme(
  base: Partial<TemplateConfig> | null | undefined,
  overrides?: Partial<TemplateConfig> | null,
): TemplateConfig {
  const merged = { ...DEFAULT_TEMPLATE_CONFIG, ...(base ?? {}), ...(overrides ?? {}) };
  const fallback = DEFAULT_TEMPLATE_CONFIG;

  const pick = <T extends string>(value: unknown, allowed: readonly T[], fall: T): T =>
    allowed.includes(value as T) ? (value as T) : fall;

  return {
    accent: typeof merged.accent === 'string' && isValidAccent(merged.accent) ? merged.accent : fallback.accent,
    bodyFont: pick(merged.bodyFont, BODY_FONTS, fallback.bodyFont),
    headingFont: pick(merged.headingFont, HEADING_FONTS, fallback.headingFont),
    layout: pick(merged.layout, SITE_LAYOUTS, fallback.layout),
    showCovers: typeof merged.showCovers === 'boolean' ? merged.showCovers : fallback.showCovers,
    density: pick(merged.density, DENSITIES, fallback.density),
    corners: pick(merged.corners, CORNERS, fallback.corners),
    paper: pick(merged.paper, PAPERS, fallback.paper),
    ornament: pick(merged.ornament, ORNAMENTS, fallback.ornament),
  };
}

/**
 * Only the tokens that differ from the theme underneath.
 *
 * Stored this way rather than as a full copy so that an admin retuning a theme
 * still reaches the writers using it: someone who changed nothing but the accent
 * keeps the theme's layout when that layout is revised. A writer's override is a
 * disagreement with one decision, not a fork of all nine.
 */
export function themeDiff(base: TemplateConfig, next: TemplateConfig): Partial<TemplateConfig> {
  const diff: Partial<TemplateConfig> = {};
  for (const key of Object.keys(next) as (keyof TemplateConfig)[]) {
    if (base[key] !== next[key]) (diff as Record<string, unknown>)[key] = next[key];
  }
  return diff;
}

/** Corner radius per token, in the same unit `--radius` is declared in. */
export const CORNER_RADIUS: Record<Corner, string> = {
  sharp: '0.125rem',
  soft: '0.65rem',
  round: '1.1rem',
};
